import { FC, useCallback, useEffect, useRef, useState } from 'react';
import stylex from '@stylexjs/stylex';
import { PlusIcon } from '@shared/assets/icons';
import { type GuestInfo, type FieldInfo, validationFieldsGuest } from '@entities/leadCollector';
import { phoneNumberValidator } from '@shared/libs/phone-validator';
import { ERROR_PHONE_MESSAGE } from '@shared/constants/errorMessages';
import { AddGuestButton } from '../AddGuestButton';
import { ExtraGuestFormRow } from './ExtraGuestFormRow';
import { copyGuestWithNewIds } from '../lib/copyGuestWithNewIds';
import { styles } from '../MainGuestTab/styles';

const updateFieldInGuest = (prev: GuestInfo, instanceId: string, value: string, error = ''): GuestInfo => {
  const patch = (fieldInfo: FieldInfo) =>
    fieldInfo.instanceId === instanceId ? { ...fieldInfo, value, error } : fieldInfo;
  return {
    FIO: prev.FIO.map(patch),
    additionalFields: (prev.additionalFields ?? []).map(patch),
  };
};

export interface AdditionalGuestsFromFormBodyProps {
  maxExtraAdults: number;
  maxExtraChildren: number;
  adultTemplate: GuestInfo | null;
  childrenTemplate: GuestInfo | null;
  adultGuestTitle?: string;
  childGuestTitle?: string;
  adultButtonTitle?: string;
  childButtonTitle?: string;
  registerValidate?: (validate: () => Promise<boolean>) => void;
  onChangeGuests?: (adultGuests: GuestInfo[], childrenGuests: GuestInfo[]) => void;
}

export const AdditionalGuestsFromFormBody: FC<AdditionalGuestsFromFormBodyProps> = ({
  maxExtraAdults,
  maxExtraChildren,
  adultTemplate,
  childrenTemplate,
  adultGuestTitle = 'Взрослый гость',
  childGuestTitle = 'Маленький гость',
  adultButtonTitle = 'Взрослый',
  childButtonTitle = 'Маленький',
  registerValidate,
  onChangeGuests,
}) => {
  const [adultGuests, setAdultGuests] = useState<GuestInfo[]>([]);
  const [childrenGuests, setChildrenGuests] = useState<GuestInfo[]>([]);
  const adultRef = useRef(adultGuests);
  const childrenRef = useRef(childrenGuests);
  adultRef.current = adultGuests;
  childrenRef.current = childrenGuests;

  const guestHasError = (guestInfo: GuestInfo) => {
    const hasFioError = guestInfo.FIO.some((fieldInfo) => fieldInfo.error);
    const hasAdditionalError = (guestInfo.additionalFields ?? []).some((fieldInfo) => fieldInfo.error);
    return hasFioError || hasAdditionalError;
  };

  const validate = useCallback(async (): Promise<boolean> => {
    let hasError = false;

    const nextAdults: GuestInfo[] = [];
    for (const guestInfo of adultRef.current) {
      const copy = structuredClone(guestInfo);
      await validationFieldsGuest(copy);
      if (guestHasError(copy)) hasError = true;
      nextAdults.push(copy);
    }

    const nextChildren: GuestInfo[] = [];
    for (const guestInfo of childrenRef.current) {
      const copy = structuredClone(guestInfo);
      await validationFieldsGuest(copy);
      if (guestHasError(copy)) hasError = true;
      nextChildren.push(copy);
    }

    setAdultGuests(nextAdults);
    setChildrenGuests(nextChildren);

    return !hasError;
  }, []);

  useEffect(() => {
    registerValidate?.(validate);
    return () => registerValidate?.(undefined as unknown as () => Promise<boolean>);
  }, [registerValidate, validate]);

  useEffect(() => {
    onChangeGuests?.(adultRef.current, childrenRef.current);
  }, [adultGuests, childrenGuests, onChangeGuests]);

  const addAdult = () => {
    if (!adultTemplate || adultGuests.length >= maxExtraAdults) return;
    setAdultGuests((prev) => [...prev, copyGuestWithNewIds(adultTemplate)]);
  };

  const addChild = () => {
    if (!childrenTemplate || childrenGuests.length >= maxExtraChildren) return;
    setChildrenGuests((prev) => [...prev, copyGuestWithNewIds(childrenTemplate)]);
  };

  const removeAdult = (index: number) => {
    setAdultGuests((prev) => prev.filter((_, i) => i !== index));
  };

  const removeChild = (index: number) => {
    setChildrenGuests((prev) => prev.filter((_, i) => i !== index));
  };

  const updateAdult = (index: number, instanceId: string, value: string, error = '') => {
    setAdultGuests((prev) => {
      if (!prev[index]) return prev;
      const next = [...prev];
      next[index] = updateFieldInGuest(next[index], instanceId, value, error);
      return next;
    });
  };

  const updateChild = (index: number, instanceId: string, value: string, error = '') => {
    setChildrenGuests((prev) => {
      if (!prev[index]) return prev;
      const next = [...prev];
      next[index] = updateFieldInGuest(next[index], instanceId, value, error);
      return next;
    });
  };

  const handlePhoneChange = (type: 'adult' | 'child', index: number, instanceId: string, value: string) => {
    const masked = phoneNumberValidator(value);
    const err = masked.length && masked.replace(/\D/g, '').length < 10 ? ERROR_PHONE_MESSAGE : '';
    if (type === 'adult') updateAdult(index, instanceId, masked, err);
    else updateChild(index, instanceId, masked, err);
  };

  const showBlock = (!!maxExtraAdults && adultTemplate) || (!!maxExtraChildren && childrenTemplate);
  if (!showBlock) return null;

  const hasAddedGuests = !!adultGuests.length || !!childrenGuests.length;
  const canAddAdult = !!maxExtraAdults && adultTemplate && adultGuests.length < maxExtraAdults;
  const canAddChild = !!maxExtraChildren && childrenTemplate && childrenGuests.length < maxExtraChildren;

  return (
    <section {...stylex.props(styles.section, styles.sectionExtraGuests)}>
      <h3 {...stylex.props(styles.sectionTitle)}>Дополнительные гости</h3>
      <div {...stylex.props(styles.extraGuestsContent, !hasAddedGuests && styles.extraGuestsContentNoGap)}>
        <div {...stylex.props(styles.adultContainer)}>
          {adultGuests.map((guestInfo, guestIndex) => (
            <ExtraGuestFormRow
              key={`adult-${guestIndex}`}
              guestInfo={guestInfo}
              title={`${adultGuestTitle} ${guestIndex + 1}`}
              type="adult"
              guestIndex={guestIndex}
              onUpdate={updateAdult}
              onRemove={() => removeAdult(guestIndex)}
              onPhoneChange={handlePhoneChange}
            />
          ))}
          {childrenGuests.map((guestInfo, guestIndex) => (
            <ExtraGuestFormRow
              key={`child-${guestIndex}`}
              guestInfo={guestInfo}
              title={`${childGuestTitle} ${guestIndex + 1}`}
              type="child"
              guestIndex={guestIndex}
              onUpdate={updateChild}
              onRemove={() => removeChild(guestIndex)}
              onPhoneChange={handlePhoneChange}
            />
          ))}
        </div>
        <div {...stylex.props(styles.addGuestButtons)}>
          {canAddAdult && (
            <AddGuestButton icon={<PlusIcon height="20" width="20" />} onClick={addAdult}>
              {adultButtonTitle}
            </AddGuestButton>
          )}
          {canAddChild && (
            <AddGuestButton icon={<PlusIcon height="20" width="20" />} onClick={addChild}>
              {childButtonTitle}
            </AddGuestButton>
          )}
        </div>
      </div>
    </section>
  );
};
